import { FunctionsHttpError } from '@supabase/supabase-js'
import { z } from 'zod'

import { type AppSupabaseClient } from '@/platform/supabase/client'
import { callRpc } from '@/platform/supabase/rpc'
import { ERROR_CODE } from '@/shared/errors/codes'
import { AppError } from '@/shared/errors/envelope'

import { SESSION_HEADER } from './supabase-extraction'

/**
 * School material learning journey (D-17), client side.
 *
 * Learner operations carry the learner session token (D-16); the server takes
 * the learner from the session. Items never include answers or help content:
 * help is fetched per mode (and recorded), answers are graded by the server.
 * Everything returned is validated here.
 */

const blockSchema = z.object({
  block_ref: z.string(),
  page: z.number().int(),
  kind: z.string(),
  resolution: z.string(),
  extracted_status: z.string(),
  text: z.string().nullable(),
})

const sourceSchema = z.object({
  source_id: z.uuid(),
  subject: z.string(),
  provider_id: z.string(),
  page_count: z.number().int(),
  confirmed_at: z.string(),
  text_deleted: z.boolean(),
  blocks: z.array(blockSchema),
})
export type MaterialSource = z.infer<typeof sourceSchema>

const itemSchema = z.object({
  item_id: z.uuid(),
  position: z.number().int(),
  kind: z.enum(['fraction', 'choice', 'text_choice', 'gap_fill']),
  prompt: z.string(),
  options: z.array(z.object({ id: z.string(), text: z.string() })).nullable(),
  origin: z.enum(['from_source', 'related']),
  source_block_refs: z.array(z.string()),
  /** English only: the one evidence dimension the item measures (never combined). */
  dimension: z.enum(['recognition', 'recall', 'contextual_use']).optional(),
  objective_code: z.string().optional(),
})
export type MaterialItem = z.infer<typeof itemSchema>

const attemptSchema = z.object({
  item_id: z.uuid(),
  answer: z.string(),
  is_correct: z.boolean(),
  mistake_tag: z.string().nullable(),
  feedback: z.string(),
  correct_display: z.string(),
  explanation: z.string(),
  help_used: z.array(z.string()),
  submitted_at: z.string(),
})
export type MaterialAttempt = z.infer<typeof attemptSchema>

const explanationSchema = z.object({
  key_idea: z.string(),
  /** Mathematics (D-21 Thinking Toolbox): the rule or formula. Absent in older sets. */
  rule: z.string().optional(),
  steps: z.array(z.string()),
  example: z.object({
    problem: z.string(),
    steps: z.array(z.string()),
    answer: z.string(),
  }),
  common_mistake: z.string(),
  source_block_refs: z.array(z.string()),
  supplementary_note: z.string(),
  /** English only (D-20 §3.2): Vietnamese support for an English explanation. */
  support_vi: z
    .object({
      key_idea: z.string(),
      steps: z.array(z.string()),
      common_mistake: z.string(),
      note: z.string(),
    })
    .optional(),
})
export type MaterialExplanation = z.infer<typeof explanationSchema>

export const practiceSchema = z.object({
  set_id: z.uuid(),
  topic_code: z.string(),
  topic_label: z.string(),
  objective: z.string(),
  explanation: explanationSchema,
  explanation_origin: z.enum(['ai', 'pre_authored']),
  created_at: z.string(),
  completed_at: z.string().nullable(),
  source: sourceSchema,
  /** Empty when an older set held only the learner's own exercise (server-filtered). */
  items: z.array(itemSchema),
  attempts: z.array(attemptSchema),
  help_opened: z.array(z.object({ item_id: z.uuid(), mode: z.string() })),
  /** English only: whether the content was reviewed by a person. */
  content_status: z.enum(['fixture_unreviewed', 'reviewed']).optional(),
})
export type MaterialPractice = z.infer<typeof practiceSchema>

const listSchema = z.array(
  z.object({
    set_id: z.uuid(),
    topic_label: z.string(),
    created_at: z.string(),
    completed_at: z.string().nullable(),
    items: z.number().int(),
    answered: z.number().int(),
  }),
)
export type MaterialPracticeSummary = z.infer<typeof listSchema>[number] & {
  /** The set's topic, read from the learner view; `null` for a finished set. */
  readonly topic_code: string | null
}

const helpSchema = z.object({
  mode: z.enum(['explain', 'hint', 'guided', 'full']),
  content: z.union([z.string(), z.array(z.string())]),
})
export type HelpMode = z.infer<typeof helpSchema>['mode']
export type HelpContent = z.infer<typeof helpSchema>

const submitSchema = z.object({
  replay: z.boolean(),
  already_submitted: z.boolean(),
  result: attemptSchema,
})

export const activitySchema = z.array(
  z.object({
    set_id: z.uuid(),
    topic_label: z.string(),
    objective: z.string(),
    explanation_origin: z.enum(['ai', 'pre_authored']),
    created_at: z.string(),
    completed_at: z.string().nullable(),
    source: sourceSchema,
    items: z.array(itemSchema),
    attempts: z.array(attemptSchema),
  }),
)
export type MaterialActivity = z.infer<typeof activitySchema>[number]

/** Body of `material-learning`: the confirmed source of one upload. */
export interface StartLearningInput {
  readonly request_id: string
  /** Absent means Mathematics. */
  readonly subject?: 'mathematics' | 'english'
  readonly source: {
    readonly document_sha256: string
    readonly mime_type: string
    readonly page_count: number
    readonly provider_id: string
    readonly extraction_id: string
    readonly extracted_at: string
  }
  readonly blocks: readonly {
    readonly block_ref: string
    readonly page: number
    readonly region: { x: number; y: number; width: number; height: number } | null
    readonly kind: string
    readonly extracted_status: string
    readonly resolution: 'accepted' | 'corrected' | 'excluded'
    readonly text: string | null
  }[]
}

export type StartFailure =
  | 'learner_session_invalid'
  | 'upload_not_found'
  | 'unsupported_topic'
  /** Mathematics: not enough practice independent of the learner's exercise (D-21). */
  | 'practice_unavailable'
  | 'too_little_text'
  | 'content_not_reviewed'
  | 'bad_request'
  | 'service_unavailable'
  | 'network'

export class StartLearningError extends Error {
  readonly code: StartFailure
  constructor(code: StartFailure) {
    super(`material learning could not start: ${code}`)
    this.name = 'StartLearningError'
    this.code = code
  }
}

const startResponseSchema = z.discriminatedUnion('ok', [
  z.object({ ok: z.literal(true), set_id: z.uuid(), created: z.boolean() }),
  z.object({
    ok: z.literal(false),
    error: z.enum([
      'learner_session_invalid',
      'upload_not_found',
      'unsupported_topic',
      'practice_unavailable',
      'too_little_text',
      'content_not_reviewed',
      'bad_request',
      'service_unavailable',
      'not_authenticated',
    ]),
  }),
])

/**
 * What the learner may see of a stored set (User Features UP-05, UP-09;
 * D-21). Sets saved before concept-first (migration 20261109000001) may hold
 * the learner's own exercise as `from_source` items. They are dropped here
 * with their answers and help, so the child flow never shows, counts or
 * submits them. A set may be left with no items: the lesson shows that state.
 * The stored set is not changed; the parent view reads it as it is.
 */
export function learnerPracticeView(p: MaterialPractice): MaterialPractice {
  const own = new Set(
    p.items.filter((i) => i.origin === 'from_source').map((i) => i.item_id),
  )
  if (own.size === 0) return p
  return {
    ...p,
    items: p.items.filter((i) => !own.has(i.item_id)),
    attempts: p.attempts.filter((a) => !own.has(a.item_id)),
    help_opened: p.help_opened.filter((h) => !own.has(h.item_id)),
  }
}

/** Learner operations, already bound to the learner session. */
export interface MaterialLearner {
  start(input: StartLearningInput): Promise<string>
  getPractice(setId: string): Promise<MaterialPractice>
  listMine(): Promise<MaterialPracticeSummary[]>
  openHelp(itemId: string, mode: HelpMode): Promise<HelpContent>
  submit(
    itemId: string,
    answer: string,
    clientAttemptId: string,
  ): Promise<MaterialAttempt>
  complete(setId: string): Promise<void>
}

export interface MaterialLearningApi {
  forSession(token: string): MaterialLearner
  /** Parent Learning Insight: persisted facts for a learner of the family. */
  activity(learnerId: string): Promise<MaterialActivity[]>
}

export function createMaterialLearningApi(
  client: AppSupabaseClient,
): MaterialLearningApi {
  return {
    forSession: (token) => {
      const getPractice = async (setId: string) =>
        learnerPracticeView(
          await callRpc(
            client,
            'get_material_practice',
            { p_session_token: token, p_set_id: setId },
            practiceSchema,
          ),
        )
      return {
        async start(input) {
          let data: unknown
          let error: unknown
          try {
            const res = await client.functions.invoke<unknown>('material-learning', {
              method: 'POST',
              body: input,
              headers: { [SESSION_HEADER]: token },
            })
            data = res.data
            error = res.error
          } catch {
            throw new StartLearningError('network')
          }
          let body: unknown = data
          if (error instanceof FunctionsHttpError && error.context instanceof Response) {
            body = await error.context.json().catch(() => null)
          } else if (error !== null && error !== undefined)
            throw new StartLearningError('network')
          const parsed = startResponseSchema.safeParse(body)
          if (!parsed.success) throw new StartLearningError('service_unavailable')
          if (!parsed.data.ok)
            throw new StartLearningError(
              parsed.data.error === 'not_authenticated'
                ? 'learner_session_invalid'
                : parsed.data.error,
            )
          return parsed.data.set_id
        },
        getPractice,
        // The server counts every item of a set. An unfinished set is counted
        // again from the learner view, so the resume list never counts the
        // learner's own exercise; one that cannot be read is left out.
        // Finished sets keep the server counts (the resume list skips them).
        // The learner view also gives the topic code of each unfinished set,
        // so a new upload of the same topic can offer to continue it.
        async listMine(): Promise<MaterialPracticeSummary[]> {
          const list = await callRpc(
            client,
            'list_my_material_practice',
            { p_session_token: token },
            listSchema,
          )
          const counted = await Promise.all(
            list.map(async (s) => {
              if (s.completed_at !== null) return { ...s, topic_code: null }
              try {
                const p = await getPractice(s.set_id)
                return {
                  ...s,
                  topic_code: p.topic_code,
                  items: p.items.length,
                  answered: p.attempts.length,
                }
              } catch {
                return null
              }
            }),
          )
          return counted.filter((s) => s !== null)
        },
        openHelp: (itemId, mode) =>
          callRpc(
            client,
            'open_material_help',
            { p_session_token: token, p_item_id: itemId, p_mode: mode },
            helpSchema,
          ),
        async submit(itemId, answer, clientAttemptId) {
          const r = await callRpc(
            client,
            'submit_material_answer',
            {
              p_session_token: token,
              p_item_id: itemId,
              p_answer: answer,
              p_client_attempt_id: clientAttemptId,
            },
            submitSchema,
          )
          if (r.already_submitted) throw new AppError(ERROR_CODE.ALREADY_SUBMITTED)
          return r.result
        },
        async complete(setId) {
          await callRpc(
            client,
            'complete_material_practice',
            { p_session_token: token, p_set_id: setId },
            z.object({ completed: z.boolean() }),
          )
        },
      }
    },
    activity: (learnerId) =>
      callRpc(
        client,
        'get_learner_material_activity',
        { p_learner_id: learnerId },
        activitySchema,
      ),
  }
}
